import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { formatDate } from "../../utils/date";
import { capitalizeFirstLetter } from "../../utils/letters";
import {
	INVALID_RUT_MESSAGE,
	RUT_DIGITS_HINT,
	cleanRut,
	commitRutField,
	formatRut,
	sanitizeRutDigitsOnly,
	toApiRut,
	validateRut,
} from "../../utils/rutUtils";
import {
	createCompany,
	deleteCompany,
	listCompanies,
	updateCompany,
} from "../api/companies";
import Modal from "../components/Modal";
import ConfirmModal from "../components/ConfirmModal";
import StatusBadge from "../components/StatusBadge";

const emptyForm = { rut: "", name: "", is_active: true };
const REQUIRED_RUT_MESSAGE = "El RUT es obligatorio.";
const REQUIRED_COMPANY_NAME_MESSAGE = "La razón social es obligatoria.";
const ROWS_PER_PAGE_DEFAULT = 10;
const COMPANIES_ALLOWED_ROWS = [10, 25, 50, 100];

const COMPANY_SORT_OPTIONS = [
	{ value: "rut", label: "RUT" },
	{ value: "name", label: "Razón social" },
	{ value: "created_at", label: "Fecha de alta" },
	{ value: "is_active", label: "Activa" },
];

const COMPANIES_ALLOWED_SORTS = new Set(
	COMPANY_SORT_OPTIONS.map((option) => option.value),
);
const COMPANIES_ALLOWED_DIRECTIONS = new Set(["asc", "desc"]);

function parsePositiveInt(rawValue, fallbackValue) {
	const parsed = Number(rawValue);
	if (!Number.isInteger(parsed) || parsed < 1) return fallbackValue;
	return parsed;
}

function parseRows(rawValue) {
	const parsed = Number(rawValue);
	if (!COMPANIES_ALLOWED_ROWS.includes(parsed)) return ROWS_PER_PAGE_DEFAULT;
	return parsed;
}

function parseSort(rawValue) {
	if (!COMPANIES_ALLOWED_SORTS.has(rawValue)) return "name";
	return rawValue;
}

function parseDirection(rawValue) {
	if (!COMPANIES_ALLOWED_DIRECTIONS.has(rawValue)) return "asc";
	return rawValue;
}

function normalizeRutDuplicateMessage(message) {
	if (message === "Ya existe Company con este rut.") {
		return "Ya existe compañia con este rut.";
	}
	return message;
}

function escapeRegExp(value) {
	return value.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

function normalizeRutLike(value) {
	return (value ?? "")
		.toString()
		.toLowerCase()
		.replaceAll(/[.\-\s]/g, "");
}

function highlightSearchMatch(value, query) {
	const text = (value ?? "").toString();
	const needle = query.trim();
	if (!needle) return text;

	const matcher = new RegExp(`(${escapeRegExp(needle)})`, "ig");
	const parts = text.split(matcher);
	let cursor = 0;

	return parts.map((part) => {
		const key = `${cursor}-${part}`;
		cursor += part.length;
		if (part.toLowerCase() === needle.toLowerCase()) {
			return (
				<mark className="search-highlight" key={`hit-${key}`}>
					{part}
				</mark>
			);
		}
		return <span key={`txt-${key}`}>{part}</span>;
	});
}

function highlightRutSearchMatch(value, query) {
	const text = (value ?? "").toString();
	const needle = query.trim();
	if (!needle) return text;

	const normalizedNeedle = normalizeRutLike(needle);
	if (!normalizedNeedle) return highlightSearchMatch(text, needle);

	const normalizedText = [];
	const originalIndexes = [];
	for (let idx = 0; idx < text.length; idx += 1) {
		const char = text[idx];
		if (/[.\-\s]/.test(char)) continue;
		normalizedText.push(char.toLowerCase());
		originalIndexes.push(idx);
	}

	const compact = normalizedText.join("");
	const start = compact.indexOf(normalizedNeedle);
	if (start === -1) return highlightSearchMatch(text, needle);

	const originalStart = originalIndexes[start];
	const originalEnd =
		originalIndexes[start + normalizedNeedle.length - 1] + 1;

	const before = text.slice(0, originalStart);
	const hit = text.slice(originalStart, originalEnd);
	const after = text.slice(originalEnd);

	return (
		<>
			<span>{before}</span>
			<mark className="search-highlight">{hit}</mark>
			<span>{after}</span>
		</>
	);
}

export default function CompaniesList() {
	const [searchParams, setSearchParams] = useSearchParams();
	const [items, setItems] = useState([]);
	const [search, setSearch] = useState(() => searchParams.get("q") || "");
	const [loading, setLoading] = useState(false);
	const [rowsPerPage, setRowsPerPage] = useState(() =>
		parseRows(searchParams.get("rows")),
	);
	const [currentPage, setCurrentPage] = useState(() =>
		parsePositiveInt(searchParams.get("page"), 1),
	);
	const [sortField, setSortField] = useState(() =>
		parseSort(searchParams.get("sort")),
	);
	const [sortDirection, setSortDirection] = useState(() =>
		parseDirection(searchParams.get("dir")),
	);
	const [showInactive, setShowInactive] = useState(
		() => searchParams.get("inactive") === "1",
	);

	const [editing, setEditing] = useState(null); // company object | null
	const [form, setForm] = useState(emptyForm);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [rutError, setRutError] = useState("");
	const [fieldErrors, setFieldErrors] = useState({});
	const [modalOpen, setModalOpen] = useState(false);
	const [deletingId, setDeletingId] = useState(null);
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [activateTarget, setActivateTarget] = useState(null);

	async function refresh() {
		setLoading(true);
		setError("");
		try {
			const data = await listCompanies();
			// DRF router devuelve array simple (sin paginacion) en tu config actual
			setItems(Array.isArray(data) ? data : (data.results ?? []));
		} catch (e) {
			setError("No se pudieron cargar las organizaciones.");
		} finally {
			setLoading(false);
		}
	}

	const filteredItems = useMemo(() => {
		const statusFiltered = showInactive
			? items
			: items.filter((item) => item.is_active);
		const query = search.trim().toLowerCase();
		if (!query) return statusFiltered;
		const normalizedQuery = normalizeRutLike(query);

		return statusFiltered.filter((item) => {
			const rutValue = (item.rut ?? "").toLowerCase();
			const nameValue = (item.name ?? "").toLowerCase();
			const formattedRut = formatRut(item.rut) || item.rut || "";
			const normalizedRut = normalizeRutLike(formattedRut);
			return (
				rutValue.includes(query) ||
				nameValue.includes(query) ||
				(normalizedQuery.length > 0 &&
					normalizedRut.includes(normalizedQuery))
			);
		});
	}, [items, search, showInactive]);

	const sortedItems = useMemo(() => {
		const data = [...filteredItems];
		data.sort((left, right) => {
			if (left.is_active !== right.is_active) {
				return left.is_active ? -1 : 1;
			}

			if (sortField === "is_active") {
				const leftName = (left.name ?? "").toString().toLowerCase();
				const rightName = (right.name ?? "").toString().toLowerCase();
				if (leftName === rightName) return 0;
				return leftName > rightName ? 1 : -1;
			}

			if (sortField === "created_at") {
				const leftValue = new Date(left.created_at ?? 0).getTime();
				const rightValue = new Date(right.created_at ?? 0).getTime();
				return sortDirection === "asc"
					? leftValue - rightValue
					: rightValue - leftValue;
			}

			const leftValue = (left[sortField] ?? "").toString().toLowerCase();
			const rightValue = (right[sortField] ?? "")
				.toString()
				.toLowerCase();
			if (leftValue === rightValue) return 0;
			if (sortDirection === "asc") {
				return leftValue > rightValue ? 1 : -1;
			}
			return leftValue < rightValue ? 1 : -1;
		});
		return data;
	}, [filteredItems, sortDirection, sortField]);

	const totalRows = sortedItems.length;
	const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

	useEffect(() => {
		const nextSearch = searchParams.get("q") || "";
		const nextRows = parseRows(searchParams.get("rows"));
		const nextPage = parsePositiveInt(searchParams.get("page"), 1);
		const nextSort = parseSort(searchParams.get("sort"));
		const nextDirection = parseDirection(searchParams.get("dir"));
		const nextShowInactive = searchParams.get("inactive") === "1";

		setSearch((prev) => (prev === nextSearch ? prev : nextSearch));
		setRowsPerPage((prev) => (prev === nextRows ? prev : nextRows));
		setCurrentPage((prev) => (prev === nextPage ? prev : nextPage));
		setSortField((prev) => (prev === nextSort ? prev : nextSort));
		setSortDirection((prev) =>
			prev === nextDirection ? prev : nextDirection,
		);
		setShowInactive((prev) =>
			prev === nextShowInactive ? prev : nextShowInactive,
		);
	}, [searchParams]);

	useEffect(() => {
		setCurrentPage(1);
	}, [search, rowsPerPage, sortField, sortDirection, showInactive]);

	useEffect(() => {
		if (currentPage > totalPages) {
			setCurrentPage(totalPages);
		}
	}, [currentPage, totalPages]);

	useEffect(() => {
		const nextParams = new URLSearchParams(searchParams);

		if (search.trim()) nextParams.set("q", search.trim());
		else nextParams.delete("q");

		if (rowsPerPage !== ROWS_PER_PAGE_DEFAULT) {
			nextParams.set("rows", String(rowsPerPage));
		} else {
			nextParams.delete("rows");
		}

		if (currentPage !== 1) nextParams.set("page", String(currentPage));
		else nextParams.delete("page");

		if (sortField !== "name") nextParams.set("sort", sortField);
		else nextParams.delete("sort");

		if (sortDirection !== "asc") nextParams.set("dir", sortDirection);
		else nextParams.delete("dir");

		if (showInactive) nextParams.set("inactive", "1");
		else nextParams.delete("inactive");

		if (nextParams.toString() !== searchParams.toString()) {
			setSearchParams(nextParams, { replace: true });
		}
	}, [
		search,
		rowsPerPage,
		currentPage,
		sortField,
		sortDirection,
		showInactive,
		searchParams,
		setSearchParams,
	]);

	function resetTableState() {
		setSearch("");
		setRowsPerPage(ROWS_PER_PAGE_DEFAULT);
		setCurrentPage(1);
		setSortField("name");
		setSortDirection("asc");
		setShowInactive(false);
		setSearchParams(new URLSearchParams(), { replace: true });
	}

	const startIndex = (currentPage - 1) * rowsPerPage;
	const endIndex = Math.min(startIndex + rowsPerPage, totalRows);
	const visibleItems = sortedItems.slice(
		startIndex,
		startIndex + rowsPerPage,
	);

	const hasSearchQuery = search.trim().length > 0;
	const hiddenInactiveCount = showInactive
		? 0
		: items.filter((item) => !item.is_active).length;
	const emptyCompanyMessage = hasSearchQuery
		? "No hay resultados para la búsqueda actual."
		: items.length === 0
			? "No hay organizaciones registradas."
			: hiddenInactiveCount > 0
				? "No hay organizaciones activas. Activa la opción para mostrar desactivadas."
				: "No hay resultados para los filtros actuales.";

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function validateForm() {
		const err = {};
		if (!(form.rut ?? "").trim()) err.rut = REQUIRED_RUT_MESSAGE;
		else if (!validateRut(form.rut).valid) {
			err.rut = INVALID_RUT_MESSAGE;
		}
		if (!(form.name ?? "").trim()) err.name = REQUIRED_COMPANY_NAME_MESSAGE;
		setFieldErrors(err);
		if (err.rut) setRutError(err.rut);
		else setRutError("");
		return Object.keys(err).length === 0;
	}

	function getApiFieldErrors(data) {
		const next = {};
		if (!data || typeof data !== "object") return next;

		const rutMsgRaw = Array.isArray(data.rut)
			? data.rut[0]
			: typeof data.rut === "string"
				? data.rut
				: "";
		const rutMsg = normalizeRutDuplicateMessage(rutMsgRaw);
		const nameMsg = Array.isArray(data.name)
			? data.name[0]
			: typeof data.name === "string"
				? data.name
				: "";

		if (rutMsg) next.rut = rutMsg;
		if (nameMsg) next.name = nameMsg;

		const detailRaw =
			typeof data.detail === "string"
				? data.detail
				: Array.isArray(data.non_field_errors)
					? data.non_field_errors[0]
					: "";
		const detail = normalizeRutDuplicateMessage(detailRaw);

		if (!next.rut && /\brut\b/i.test(detail)) next.rut = detail;
		if (!next.name && /(razon\s+social|\bnombre\b)/i.test(detail)) {
			next.name = detail;
		}

		return next;
	}

	async function onSubmit(e) {
		e.preventDefault();
		if (!validateForm()) return;

		const normalizedRut = toApiRut(form.rut);

		setSaving(true);
		setError("");
		setSuccess("");
		try {
			if (editing) {
				await updateCompany(editing.id, {
					...form,
					rut: normalizedRut,
				});
			} else {
				await createCompany({ ...form, rut: normalizedRut });
			}
			setEditing(null);
			setForm(emptyForm);
			setRutError("");
			setFieldErrors({});
			setModalOpen(false);
			setSuccess(
				editing
					? "Organización actualizada correctamente."
					: "Organización creada correctamente.",
			);
			await refresh();
		} catch (e) {
			const apiData = e.response?.data;
			const apiFieldErrors = getApiFieldErrors(apiData);
			setFieldErrors((prev) => ({ ...prev, ...apiFieldErrors }));

			if (apiFieldErrors.rut) {
				setRutError(apiFieldErrors.rut);
			}

			const fallbackMsg =
				typeof apiData?.detail === "string"
					? apiData.detail
					: "No se pudo guardar. Verifica RUT único y campos obligatorios.";

			if (!apiFieldErrors.rut && !apiFieldErrors.name) {
				setError(fallbackMsg);
			}
			setSuccess("");
		} finally {
			setSaving(false);
		}
	}

	function startCreate() {
		setEditing(null);
		setForm(emptyForm);
		setRutError("");
		setFieldErrors({});
		setError("");
		setSuccess("");
		setModalOpen(true);
	}

	function startEdit(c) {
		setEditing(c);
		setForm({
			rut: sanitizeRutDigitsOnly(cleanRut(c.rut ?? "")),
			name: c.name,
			is_active: c.is_active,
		});
		setRutError("");
		setFieldErrors({});
		setError("");
		setSuccess("");
		setModalOpen(true);
	}

	function askDeactivateCompany(company) {
		setDeleteTarget(company);
	}

	function askActivateCompany(company) {
		setActivateTarget(company);
	}

	async function confirmDeactivateCompany() {
		if (!deleteTarget) return;

		setDeletingId(deleteTarget.id);
		setError("");
		setSuccess("");
		try {
			await deleteCompany(deleteTarget.id, {
				confirm_delete: true,
			});
			setDeleteTarget(null);
			setSuccess("Organización desactivada correctamente.");
			await refresh();
		} catch (e) {
			const msg =
				typeof e?.response?.data?.detail === "string"
					? e.response.data.detail
					: "No se pudo desactivar la organización.";
			setError(msg);
		} finally {
			setDeletingId(null);
		}
	}

	async function confirmActivateCompany() {
		if (!activateTarget) return;
		setDeletingId(activateTarget.id);
		setError("");
		setSuccess("");
		try {
			await updateCompany(activateTarget.id, { is_active: true });
			setActivateTarget(null);
			setSuccess("Organización activada correctamente.");
			await refresh();
		} catch (e) {
			const msg =
				typeof e?.response?.data?.detail === "string"
					? e.response.data.detail
					: "No se pudo activar la organización.";
			setError(msg);
		} finally {
			setDeletingId(null);
		}
	}

	let companyRows;
	if (loading) {
		companyRows = (
			<tr>
				<td colSpan="5" className="text-muted p-4">
					<span>Cargando...</span>
				</td>
			</tr>
		);
	} else if (visibleItems.length === 0) {
		companyRows = (
			<tr>
				<td colSpan="5" className="text-muted p-4">
					<span>{emptyCompanyMessage}</span>
				</td>
			</tr>
		);
	} else {
		companyRows = visibleItems.map((c) => (
			<tr key={c.id}>
				<td>
					<span>
						<strong>
							{highlightRutSearchMatch(
								formatRut(c.rut) || c.rut,
								search,
							)}
						</strong>
					</span>
				</td>
				<td>
					{c.is_active ? (
						<Link to={`/backoffice/companies/${c.id}`}>
							<span>
								{highlightSearchMatch(
									capitalizeFirstLetter(c.name),
									search,
								)}
							</span>
						</Link>
					) : (
						<span>
							{highlightSearchMatch(
								capitalizeFirstLetter(c.name),
								search,
							)}
						</span>
					)}
				</td>
				<td>
					<span>{c.created_at ? formatDate(c.created_at) : "-"}</span>
				</td>
				<td>
					<StatusBadge
						active={Boolean(c.is_active)}
						title="Organización activa"
					/>
				</td>
				<td>
					<div className="d-flex justify-content-center align-items-center gap-1 flex-nowrap">
						{c.is_active ? (
							<Link
								className="btn-sm adm-see-btn action-icon-btn text-decoration-none d-inline-flex align-items-center justify-content-center"
								to={`/backoffice/companies/${c.id}`}
								title="Ver detalle"
								aria-label="Ver detalle"
							>
								<i className="ri-eye-fill" />
							</Link>
						) : (
							<button
								type="button"
								className="btn-sm adm-see-btn action-icon-btn d-inline-flex align-items-center justify-content-center border-0"
								disabled
								title="Primero debes activar la organización."
								aria-label="Ver detalle deshabilitado"
							>
								<i className="ri-eye-fill" />
							</button>
						)}
						<button
							type="button"
							className="btn-sm adm-upd-btn action-icon-btn d-inline-flex align-items-center justify-content-center border-0"
							onClick={() => startEdit(c)}
							disabled={!c.is_active}
							title={
								c.is_active
									? "Editar"
									: "Primero debes activar la organización."
							}
							aria-label="Editar"
						>
							<i className="ri-edit-fill" />
						</button>
						<button
							type="button"
							className="btn-sm adm-del-btn action-icon-btn d-inline-flex align-items-center justify-content-center border-0"
							onClick={() => askDeactivateCompany(c)}
							disabled={!c.is_active || deletingId !== null}
							title={
								c.is_active
									? "Desactivar"
									: "La organización ya está desactivada."
							}
							aria-label="Desactivar"
						>
							<i
								className={
									deletingId === c.id
										? "ri-loader-4-line"
										: "ri-close-circle-line"
								}
							/>
						</button>
						<button
							type="button"
							className="btn-sm adm-upd-btn action-icon-btn d-inline-flex align-items-center justify-content-center border-0"
							onClick={() => askActivateCompany(c)}
							disabled={c.is_active || deletingId !== null}
							title={
								c.is_active
									? "La organización ya está activa."
									: "Activar"
							}
							aria-label="Activar"
						>
							<i
								className={
									deletingId === c.id
										? "ri-loader-4-line"
										: "ri-checkbox-circle-line"
								}
							/>
						</button>
					</div>
				</td>
			</tr>
		));
	}

	return (
		<div className="container-fluid">
			<div className="container-principal">
				<div>
					<h2 className="m-0">Organizaciones</h2>
				</div>

				<div className="admin-actions-wrapper w-100">
					<button
						className="admin-btn text-nowrap"
						onClick={startCreate}
					>
						<span>Nueva organización</span>
					</button>
				</div>
			</div>

			{success && (
				<div className="alert alert-success mt-3 mb-0" role="alert">
					<span>{success}</span>
				</div>
			)}

			{error && (
				<div className="alert alert-danger mt-3 mb-0" role="alert">
					<span>{error}</span>
				</div>
			)}

			<div className="card mt-3 shadow-sm">
				<div className="card-body p-0">
					<div className="table-toolbar table-toolbar-locked">
						<div className="toolbar-group toolbar-pagination-group">
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => setCurrentPage(1)}
								disabled={currentPage === 1}
							>
								&lt;&lt;
							</button>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() =>
									setCurrentPage((prev) =>
										Math.max(1, prev - 1),
									)
								}
								disabled={currentPage === 1}
							>
								&lt;
							</button>
							<select
								className="form-select toolbar-select toolbar-page-select"
								value={currentPage}
								onChange={(e) =>
									setCurrentPage(Number(e.target.value))
								}
								disabled={totalPages <= 1}
							>
								{Array.from(
									{ length: totalPages },
									(_, idx) => idx + 1,
								).map((page) => (
									<option key={page} value={page}>
										{page}
									</option>
								))}
							</select>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() =>
									setCurrentPage((prev) =>
										Math.min(totalPages, prev + 1),
									)
								}
								disabled={currentPage >= totalPages}
							>
								&gt;
							</button>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => setCurrentPage(totalPages)}
								disabled={currentPage >= totalPages}
							>
								&gt;&gt;
							</button>
						</div>

						<div className="search-group toolbar-search-group d-flex align-items-center gap-2">
							<input
								className="form-control"
								value={search} onChange={(e) => setSearch(e.target.value)}
								placeholder="Buscar por RUT o nombre de la organización"
								maxLength={120}
							/>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => {
									setSearch("");
									setCurrentPage(1);
								}}
								disabled={!search.trim()}
								title="Limpiar búsqueda"
								aria-label="Limpiar búsqueda"
							>
								<i className="ri-eraser-line" />
							</button>
						</div>

						<div className="toolbar-group toolbar-controls-group">
							<label className="d-flex align-items-center gap-2 mb-0">
								<span>Número de filas:</span>
								<select
									className="form-select toolbar-select"
									value={rowsPerPage}
									onChange={(e) =>
										setRowsPerPage(Number(e.target.value))
									}
								>
									<option value={10}>10</option>
									<option value={25}>25</option>
									<option value={50}>50</option>
									<option value={100}>100</option>
								</select>
							</label>

							<label className="d-flex align-items-center gap-2 mb-0">
								<span>Ordenar por:</span>
								<select
									className="form-select toolbar-select"
									value={sortField}
									onChange={(e) =>
										setSortField(e.target.value)
									}
								>
									{COMPANY_SORT_OPTIONS.map((option) => (
										<option
											key={option.value}
											value={option.value}
										>
											{option.label}
										</option>
									))}
								</select>
							</label>

							<button
								type="button"
								className="toolbar-order-btn"
								onClick={() =>
									setSortDirection((prev) =>
										prev === "asc" ? "desc" : "asc",
									)
								}
							>
								{sortDirection === "asc" ? "Asc" : "Desc"}
							</button>

							<label className="d-flex align-items-center gap-2 mb-0">
								<input
									className="form-check-input m-0"
									type="checkbox"
									checked={showInactive}
									onChange={(e) =>
										setShowInactive(e.target.checked)
									}
								/>
								<span>Mostrar desactivadas</span>
							</label>

							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={resetTableState}
								title="Reiniciar tabla"
							>
								Reiniciar
							</button>
						</div>
					</div>

					<div className="table-responsive">
						<table className="table table-sm table-hover mb-0 align-middle table-organizations-nowrap">
							<thead className="table-light">
								<tr>
									<th>
										<span>RUT</span>
									</th>
									<th>
										<span>Razon Social</span>
									</th>
									<th>
										<span>Fecha de alta</span>
									</th>
									<th>
										<span>Activa</span>
									</th>
									<th
										className="text-center"
										style={{ width: 320 }}
									>
										<span>Acciones</span>
									</th>
								</tr>
							</thead>
							<tbody>{companyRows}</tbody>
						</table>
					</div>
					<div className="table-footer-summary">
						<span>
							Mostrando {totalRows === 0 ? 0 : startIndex + 1} -{" "}
							{endIndex} de {totalRows} registros
						</span>
					</div>
				</div>
			</div>

			<Modal
				title={
					editing
						? "Modificar datos de la organización"
						: "Nueva organización"
				}
				open={modalOpen}
				onClose={() => {
					if (saving) return;
					setModalOpen(false);
				}}
				footer={
					<>
						<button
							type="button"
							className="adm-del-btn"
							onClick={() => setModalOpen(false)}
							disabled={saving}
						>
							<span>Cancelar</span>
						</button>
						<button
							type="button"
							className="adm-save-btn"
							onClick={onSubmit}
							disabled={saving}
						>
							<span>{saving ? "Guardando..." : "Guardar"}</span>
						</button>
					</>
				}
			>
				<form onSubmit={onSubmit} className="d-flex flex-column gap-3">
					<div>
						<label className="form-label">
							<span>RUT de la organización </span>
							<span className="text-danger">*</span>
						</label>
						<input
							className={`form-control ${rutError || fieldErrors.rut ? "is-invalid" : ""}`}
							value={form.rut} onChange={(e) => {
								setForm((p) => ({
									...p,
									rut: sanitizeRutDigitsOnly(e.target.value),
								}));
								if (rutError || fieldErrors.rut) {
									setRutError("");
									setFieldErrors((p) => ({
										...p,
										rut: undefined,
									}));
								}
							}}
							onBlur={() => {
								if (!form.rut?.trim()) {
									setRutError(REQUIRED_RUT_MESSAGE);
									setFieldErrors((p) => ({
										...p,
										rut: REQUIRED_RUT_MESSAGE,
									}));
									return;
								}
								const r = commitRutField(form.rut);
								if (!r.ok) {
									setRutError(
										r.message || INVALID_RUT_MESSAGE,
									);
									setFieldErrors((p) => ({
										...p,
										rut: r.message || INVALID_RUT_MESSAGE,
									}));
								} else {
									setForm((p) => ({
										...p,
										rut: r.formatted,
									}));
									setRutError("");
									setFieldErrors((p) => ({
										...p,
										rut: undefined,
									}));
								}
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									e.currentTarget.blur();
								}
							}}
							required
							maxLength={10}
							placeholder="Ej: 123456789"
							title={RUT_DIGITS_HINT}
							aria-invalid={!!(rutError || fieldErrors.rut)}
						/>
						<p className="form-text small text-muted mb-0">
							{RUT_DIGITS_HINT}
						</p>
						{(rutError || fieldErrors.rut) && (
							<div className="invalid-feedback">
								{rutError || fieldErrors.rut}
							</div>
						)}
					</div>

					<div>
						<label className="form-label">
							<span>Razon Social</span>{" "}
							<span className="text-danger">*</span>
						</label>
						<input
							className={`form-control ${fieldErrors.name ? "is-invalid" : ""}`}
							value={form.name} onChange={(e) => {
								setForm((p) => ({
									...p,
									name: e.target.value,
								}));
								if (fieldErrors.name)
									setFieldErrors((p) => ({
										...p,
										name: undefined,
									}));
							}}
							onBlur={() => {
								if (!(form.name ?? "").trim()) {
									setFieldErrors((p) => ({
										...p,
										name: REQUIRED_COMPANY_NAME_MESSAGE,
									}));
								}
							}}
							placeholder="Ej: Comercial Acme SpA"
							maxLength={255}
							aria-invalid={!!fieldErrors.name}
						/>
						{fieldErrors.name && (
							<div className="invalid-feedback">
								{fieldErrors.name}
							</div>
						)}
					</div>

					<div className="form-check mt-1">
						<input
							className="form-check-input"
							type="checkbox"
							checked={form.is_active}
							onChange={(e) =>
								setForm((p) => ({
									...p,
									is_active: e.target.checked,
								}))
							}
							id="companyActive"
						/>
						<label
							className="form-check-label"
							htmlFor="companyActive"
						>
							<span>Organización activa</span>
						</label>
					</div>

					<button type="submit" className="d-none">
						<span>enviar</span>
					</button>
				</form>
			</Modal>

			<ConfirmModal
				open={!!deleteTarget}
				onClose={() => {
					if (deletingId !== null) return;
					setDeleteTarget(null);
				}}
				onConfirm={confirmDeactivateCompany}
				title="Desactivar organización"
				message={
					deleteTarget
						? `Estas seguro de desactivar la organización "${deleteTarget.name}"? Se conservara su historial y podra reactivarse si vuelve a trabajar con Somnus.`
						: ""
				}
				confirmLabel={
					deletingId !== null ? "Desactivando..." : "Desactivar"
				}
				loading={deletingId !== null}
			/>
			<ConfirmModal
				open={!!activateTarget}
				onClose={() => {
					if (deletingId !== null) return;
					setActivateTarget(null);
				}}
				onConfirm={confirmActivateCompany}
				title="Activar organización"
				message={
					activateTarget
						? `Estas seguro de activar la organización "${activateTarget.name}"? Volvera a estar disponible para operaciones del backoffice.`
						: ""
				}
				confirmLabel={
					deletingId !== null ? "Activando..." : "Activar"
				}
				loading={deletingId !== null}
			/>
		</div>
	);
}
